// <Browse> + <RoomCard> — the public-room lobby list (#browse). PURE function of `app`: the
// classic showBrowse()/fetchBrowse() write the fetched rooms array onto app.browseRooms (null
// while loading) and re-mount; this component renders the head/count/list/foot scaffold + one
// <RoomCard> per room, exactly as the old imperative renderBrowseList() string-built it.
// Preact DIFFS instead of clobbering. Emits the SAME ids / class names / data-action attrs /
// visible text so document-level click delegation + the smoke grep contract stay green
// (data-action join-public/browse-back/browse-refresh/browse-create). See PREACT-MIGRATION.md.
import { html, t } from '../shared.js';

// One public room row — mirror of the classic .room-card template (host emoji + name, badges,
// avatar pips, ready/seat count). data-action="join-public" + data-id drive document-level
// delegation → joinPublic(roomId); no per-component onClick.
function RoomCard({ room }: { room: BrowseRoom }) {
  const r = room;
  // No AI tell in the hall: `players` (total seated) drives the count + "one more"; avatar pips
  // show each seat's real emoji (the projection no longer carries a per-seat ai flag).
  const hot = Math.max(0, (r.seats || 8) - (r.players || 0)) === 1;
  const av = (r.avatars || []).slice(0, 5);
  const extra = r.players > 5 ? r.players - 5 : 0;
  return html`
    <section class="room-card" data-action="join-public" data-id=${r.roomId}>
      <div class="rc-host">${(r.host && r.host.emoji) || '🧑'}</div>
      <div class="rc-mid">
        <div class="rc-name">${t('browse.roomTitle', { host: (r.host && r.host.name) || t('browse.hostFallback') })}</div>
        <div class="rc-badges">
          <span class="rc-badge">${t('browse.ready')}</span>
          ${hot ? html`<span class="rc-badge hot">${t('browse.oneMore')}</span>` : ''}
        </div>
        <div class="rc-avs">
          ${av.map(a => html`<i>${a.emoji || '🧑'}</i>`)}
          ${extra ? html`<i>+${extra}</i>` : ''}
        </div>
      </div>
      <div class="rc-right">
        <div class="rc-count">${r.players}/${r.seats}</div>
        <div class="rc-join">${t('browse.join')}</div>
      </div>
    </section>
  `;
}

export function Browse({ app }: { app: AppState }) {
  const rooms = app ? app.browseRooms : null;
  // Loading skeletons + placeholder count (rooms===null === "正在找房间…").
  const loading = rooms == null;
  const empty = !loading && rooms.length === 0;
  const countText = loading ? t('browse.finding') : (rooms.length ? t('browse.count', { n: rooms.length }) : '');
  return html`
    <div class="browse-head">
      <button class="tag" data-action="browse-back">${t('browse.back')}</button>
      <span class="browse-title">${t('browse.publicLobby')}</span>
      <span class="chip" id="browseRefresh" data-action="browse-refresh" title=${t('browse.refresh')}>🔄</span>
    </div>
    <div class="browse-codejoin"><input id="joinCode" class="code-input" placeholder=${t('browse.codePlaceholder')} maxlength="4"/><button class="yellow" data-action="join">${t('browse.joinBtn')}</button></div>
    <div class="browse-count small muted" id="browseCount">${countText}</div>
    <div id="browseList">
      ${loading
        ? html`<div class="skel"></div><div class="skel"></div><div class="skel"></div>`
        : empty
          ? html`<div class="browse-empty"><div class="em">🪑</div><div class="ttl">${t('browse.emptyTitle')}</div><div class="sub" dangerouslySetInnerHTML=${{ __html: t('browse.emptySub') }}></div><button class="create-btn" data-action="browse-create">${t('browse.emptyCreate')}</button></div>`
          : rooms.map((r: BrowseRoom) => html`<${RoomCard} room=${r} key=${r.roomId} />`)}
    </div>
    <div class="browse-foot"><span data-action="browse-create">${t('browse.foot')}</span></div>
  `;
}
